import React, { useState, useEffect } from 'react';
import { imageService } from '../../services/imageService';
import { resolveAssetUrl } from '../../services/api';
import { useSite } from '../../context/SiteContext';

const ZONE_SPEC = {
  hero_slideshow: { slots: 3, label: 'Hero slideshow', meta: '3 ảnh · 4:3', hint: 'Slideshow chính phía bên phải hero — fade 5s tự chạy.', page: 'home' },
  home_about: { slots: 1, label: 'Về chúng tôi', meta: '1 ảnh · 4:3', hint: 'Ảnh minh hoạ cho section "Người đồng hành đáng tin cậy của nhà thuốc Việt Nam".', page: 'home' },
  home_testimonials: { slots: 3, label: 'Phản hồi nhà thuốc', meta: '3 ảnh · 4:3', hint: 'Ảnh banner top của 3 card phản hồi.', page: 'home' },
  home_csr: { slots: 1, label: 'Tủ thuốc cho em (banner)', meta: '1 ảnh · 1:1 (tròn)', hint: 'Ảnh minh hoạ cho banner CSR — hiển thị dạng hình tròn ở trang chủ.', page: 'home' },
  gioithieu_story: { slots: 1, label: 'Câu chuyện (story photo)', meta: '1 ảnh · 4:3', hint: 'Ảnh minh hoạ section "Khởi đầu từ một niềm tin".', page: 'gioi-thieu' },
  timeline: { slots: 2, label: 'Timeline lịch sử', meta: '2 ảnh · 4:3', hint: '2 ảnh aside cuộn dọc theo timeline lịch sử công ty.', page: 'gioi-thieu' },
  tuyendung_hero: { slots: 1, label: 'Hero photo', meta: '1 ảnh · 4:3', hint: 'Ảnh đội ngũ / không gian làm việc — neo góc trên-phải hero trang Tuyển dụng.', page: 'tuyen-dung' }
};

const SUBTABS = [
  { key: 'home', label: '📍 Trang chủ' },
  { key: 'gioi-thieu', label: '📍 Giới thiệu' },
  { key: 'tuyen-dung', label: '📍 Tuyển dụng' }
];

export default function ImagesManager() {
  const [imagesByZone, setImagesByZone] = useState({});
  const [loading, setLoading] = useState(true);
  const [subtab, setSubtab] = useState('home');
  const [uploadingKey, setUploadingKey] = useState(null);
  const { refreshImages } = useSite();

  const loadImages = () => {
    setLoading(true);
    imageService
      .getAll()
      .then((res) => {
        const byZone = {};
        Object.keys(ZONE_SPEC).forEach((z) => { byZone[z] = []; });
        (res.data || []).forEach((row) => {
          if (!byZone[row.zone]) byZone[row.zone] = [];
          byZone[row.zone].push(row);
        });
        setImagesByZone(byZone);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadImages();
  }, []);

  const handlePick = (zone, slot) => {
    if (uploadingKey) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (file) handleUpload(zone, slot, file);
    });
    input.click();
  };

  const handleUpload = async (zone, slot, file) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('File quá lớn (>5MB)');
      return;
    }
    const key = `${zone}:${slot}`;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const uploadRes = await imageService.upload(fd);
      await imageService.upsert({ zone, slot, url: uploadRes.data.url, alt: file.name });
      loadImages();
      refreshImages();
    } catch (err) {
      alert('Lỗi upload: ' + err.message);
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDelete = async (row, zone, slot) => {
    if (!row) return;
    if (!window.confirm(`Xoá ảnh "${ZONE_SPEC[zone].label}" slot ${slot + 1}?`)) return;
    try {
      await imageService.delete(row.id);
      loadImages();
      refreshImages();
    } catch (err) {
      alert('Lỗi xoá ảnh: ' + err.message);
    }
  };

  const zonesForSubtab = Object.keys(ZONE_SPEC).filter((z) => ZONE_SPEC[z].page === subtab);

  return (
    <section className="admin-panel">
      <div className="panel-head">
        <div>
          <h2>Ảnh site</h2>
          <p className="panel-head__sub">Upload ảnh hiển thị trên các trang. JPG/PNG/WebP &lt; 5MB.</p>
        </div>
      </div>

      <nav className="admin-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={subtab === t.key ? 'admin-subtab is-active' : 'admin-subtab'}
            onClick={() => setSubtab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <p className="muted">Đang tải…</p>
      ) : (
        <div className="image-zones">
          {zonesForSubtab.map((zone) => {
            const spec = ZONE_SPEC[zone];
            const rows = imagesByZone[zone] || [];
            const bySlot = new Map(rows.map((r) => [r.slot, r]));
            return (
              <div className="image-zone" data-zone={zone} key={zone}>
                <h3>{spec.label} <span className="zone-meta">{spec.meta}</span></h3>
                <p className="zone-hint">{spec.hint}</p>
                <div className="image-slots">
                  {Array.from({ length: spec.slots }).map((_, i) => {
                    const row = bySlot.get(i);
                    const key = `${zone}:${i}`;
                    const isUploading = uploadingKey === key;
                    if (row) {
                      return (
                        <div className={`image-slot ${isUploading ? 'is-uploading' : ''}`} key={key} onClick={() => handlePick(zone, i)}>
                          <span className="image-slot__label">Slot {i + 1}</span>
                          <button
                            className="image-slot__delete"
                            type="button"
                            title="Xoá ảnh"
                            aria-label="Xoá ảnh"
                            onClick={(e) => { e.stopPropagation(); handleDelete(row, zone, i); }}
                          >
                            ×
                          </button>
                          <img src={resolveAssetUrl(row.url)} alt={row.alt || ''} loading="lazy" />
                          <div className="image-slot__overlay">{isUploading ? 'Đang xử lý…' : 'Click để thay ảnh'}</div>
                        </div>
                      );
                    }
                    return (
                      <div className="image-slot image-slot--empty" key={key} onClick={() => handlePick(zone, i)}>
                        <div>{isUploading ? 'Đang xử lý…' : `+ Upload slot ${i + 1}`}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
